import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutContent, faqItems } from '@/mocks/about';

export default function About() {
  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: aboutContent.brandName,
    url: 'https://hqsls.cn',
    description: aboutContent.tagline,
    email: aboutContent.contact.email,
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressCountry: 'CN',
    },
    sameAs: [
      'https://weibo.com/heiliaobudayang',
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          {/* Page Header */}
          <section className="relative w-full h-[280px] md:h-[360px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=A%20modern%20minimalist%20office%20interior%20with%20warm%20ambient%20lighting%2C%20clean%20workspace%20with%20monitors%20showing%20news%20feeds%2C%20professional%20editorial%20environment%2C%20soft%20shadows%2C%20warm%20beige%20and%20wood%20tones%2C%20no%20text%2C%20no%20watermark&width=1800&height=600&seq=about-bg&orientation=landscape"
              alt="关于黑料不打烊"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center px-4 animate-fade-in">
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
                  关于我们
                </h1>
                <p className="text-white/70 text-base max-w-lg mx-auto">
                  了解黑料不打烊的品牌故事与团队
                </p>
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background-50 to-transparent"></div>
          </section>

          <div className="w-full px-4 md:px-6 py-6">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
          </div>

          {/* Brand Introduction */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-5xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
                <div className="flex-1 animate-slide-up">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                    品牌介绍
                  </h2>
                  {aboutContent.intro.map((paragraph, index) => (
                    <p key={index} className="text-foreground-700 leading-relaxed mb-4">
                      {paragraph}
                    </p>
                  ))}
                </div>
                <div className="w-full lg:w-[420px] flex-shrink-0">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20modern%20creative%20workspace%20with%20multiple%20monitors%20displaying%20entertainment%20news%20feeds%2C%20warm%20ambient%20lighting%2C%20team%20collaboration%20environment%2C%20editorial%20office%20with%20modern%20design%2C%20soft%20color%20palette%2C%20professional%20aesthetic%2C%20no%20text%2C%20no%20watermark&width=600&height=450&seq=about-brand-img&orientation=landscape"
                    alt="黑料不打烊办公室"
                    className="w-full rounded-lg"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100/70">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {aboutContent.stats.map((stat, index) => (
                  <div key={index} className="text-center p-6 rounded-lg bg-background-50 animate-slide-up" style={{ animationDelay: `${index * 0.1}s` }}>
                    <p className="text-3xl md:text-4xl font-bold text-primary-500 mb-2">{stat.value}</p>
                    <p className="text-sm text-foreground-600">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
                为什么选择黑料不打烊？
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {aboutContent.features.map((feature, index) => (
                  <div key={index} className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center group hover:border-primary-200 transition-all duration-300 animate-slide-up" style={{ animationDelay: `${index * 0.15}s` }}>
                    <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-50 group-hover:bg-primary-500 transition-colors">
                      <i className={`${feature.icon} text-2xl text-primary-500 group-hover:text-white transition-colors`}></i>
                    </div>
                    <h3 className="text-base font-bold text-foreground-900 mb-2">{feature.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Reputation / Testimonials */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100/70">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {aboutContent.testimonials.map((testimonial, index) => (
                  <div key={index} className="p-6 rounded-lg bg-background-50 border border-background-200/70 animate-slide-up" style={{ animationDelay: `${index * 0.15}s` }}>
                    <div className="flex gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      "{testimonial.content}"
                    </p>
                    <div className="flex items-center gap-2 border-t border-background-200/70 pt-4">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                        <i className="ri-user-3-line text-primary-500 text-sm"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">{testimonial.author}</p>
                        <p className="text-xs text-foreground-500">{testimonial.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
                常见问题
              </h2>
              <div className="space-y-4">
                {faqItems.map((item, index) => (
                  <details key={index} className="group rounded-lg bg-background-50 border border-background-200/70 overflow-hidden">
                    <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                      <h3 className="text-base font-semibold text-foreground-900 pr-4">{item.question}</h3>
                      <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                    </summary>
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-700 leading-relaxed">{item.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100/70">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
                联系我们
              </h2>
              <div className="flex flex-col lg:flex-row gap-10">
                <div className="flex-1 space-y-5">
                  <div className="flex items-center gap-4 p-4 rounded-lg bg-background-50">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 flex-shrink-0">
                      <i className="ri-mail-line text-primary-500 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 mb-0.5">邮箱</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutContent.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 rounded-lg bg-background-50">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 flex-shrink-0">
                      <i className="ri-wechat-line text-primary-500 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 mb-0.5">微信</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutContent.contact.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 rounded-lg bg-background-50">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 flex-shrink-0">
                      <i className="ri-weibo-line text-primary-500 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 mb-0.5">微博</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutContent.contact.weibo}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 rounded-lg bg-background-50">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-500 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 mb-0.5">地址</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutContent.contact.address}</p>
                    </div>
                  </div>
                </div>

                {/* Contact Form */}
                <div className="flex-1 bg-background-50 rounded-lg border border-background-200/70 p-6">
                  <h3 className="text-lg font-bold text-foreground-900 mb-5">给我们留言</h3>
                  <form
                    data-readdy-form=""
                    id="about-contact-form"
                    action="https://readdy.ai/api/form/d9qasume3oq4jqumtjmg"
                    method="POST"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const form = e.currentTarget;
                      const formData = new FormData(form);
                      const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value.trim();
                      if (honeypot) {
                        const successEl = document.getElementById('form-success-msg');
                        const errorEl = document.getElementById('form-error-msg');
                        if (successEl) successEl.style.display = 'block';
                        if (errorEl) errorEl.style.display = 'none';
                        form.reset();
                        return;
                      }
                      const payload = new URLSearchParams();
                      formData.forEach((value, key) => {
                        if (key !== 'website_alt') payload.append(key, value.toString());
                      });
                      try {
                        const response = await fetch('https://readdy.ai/api/form/d9qasume3oq4jqumtjmg', { method: 'POST', body: payload });
                        const responseText = await response.text();
                        let parsed: { code?: string; meta?: { message?: string }; message?: string } = {};
                        try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
                        const successEl = document.getElementById('form-success-msg');
                        const errorEl = document.getElementById('form-error-msg');
                        const errorText = document.getElementById('form-error-text');
                        if (response.ok && parsed?.code === 'OK') {
                          if (successEl) successEl.style.display = 'block';
                          if (errorEl) errorEl.style.display = 'none';
                          form.reset();
                        } else {
                          if (errorEl) errorEl.style.display = 'block';
                          if (errorText) errorText.textContent = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
                          if (successEl) successEl.style.display = 'none';
                        }
                      } catch {
                        const errorEl = document.getElementById('form-error-msg');
                        if (errorEl) errorEl.style.display = 'block';
                        const successEl = document.getElementById('form-success-msg');
                        if (successEl) successEl.style.display = 'none';
                      }
                    }}
                  >
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                          placeholder="请输入留言内容（最多500字）"
                        ></textarea>
                      </div>
                      <input
                        name="website_alt"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                      <div id="form-success-msg" className="hidden p-3 rounded-md bg-green-50 text-green-700 text-sm">
                        留言提交成功！我们会尽快回复您。
                      </div>
                      <div id="form-error-msg" className="hidden p-3 rounded-md bg-primary-50 text-primary-700 text-sm">
                        <span id="form-error-text">提交失败，请稍后重试</span>
                      </div>
                      <button
                        type="submit"
                        className="w-full px-4 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                      >
                        提交留言
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}